import { RadioGroup, Switch } from '@headlessui/react';
import {
  DocumentTextIcon,
  LockClosedIcon,
  UploadIcon,
  XIcon,
} from '@heroicons/react/outline';
import { ChevronDownIcon } from '@heroicons/react/solid';
import dynamic from 'next/dynamic';
import useLocale from 'next-translate/useTranslation';
import TextareaAutosize from 'react-textarea-autosize';

import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';
import Spinner from '@/components/Spinner';

import type { InputField, Preset } from '@/constants/presets';

import {
  fieldOptions,
  fitsAsChips,
  placeholderSuggestions,
  useTemplateTranslation,
} from './kit';
import type { UploadedFile } from './usePublicGeneration';
import type { FieldValues } from './useTemplateFields';

const MultiFileUpload = dynamic(
  () => import('@/components/atoms/MultiFileUpload'),
);
const Microphone = dynamic(() => import('@/components/presets/Microphone'));

/** The look every text control on the workspace shares. */
export const inputClass =
  'block w-full rounded-xl border-0 bg-[#fbfbfd] px-3.5 py-3 text-[15px] leading-6 text-[#10101c] shadow-[inset_0_1px_2px_rgba(15,15,40,0.04)] ring-1 ring-inset ring-[#0f0f28]/[0.12] transition placeholder:text-[#9b9bb0] hover:ring-[#0f0f28]/[0.22] focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white/[0.04] dark:text-white dark:ring-white/10 dark:placeholder:text-[#6e6e8c] dark:hover:ring-white/20 dark:focus:bg-white/[0.06] dark:focus:ring-primary-400';

export type WorkspaceUploads = {
  uploadedFiles: Record<string, UploadedFile>;
  uploadedMultiFiles: Record<string, any>;
  uploadFile: (fieldName: string, file: File) => void;
  removeFile: (fieldName: string) => void;
  setMultiFiles: (fieldName: string, files: any) => void;
  isFileLoading: boolean;
};

type Props = {
  preset: Preset;
  fields: FieldValues;
  setField: (name: string, value: any) => void;
  /** Free mode passes the upload handlers; without them uploads wait for sign-up. */
  uploads?: WorkspaceUploads;
};

export default function WorkspaceFields({
  preset,
  fields,
  setField,
  uploads,
}: Props) {
  return (
    <div className='space-y-6'>
      {preset.fields
        // Logged-out runs make no post image, so its reference uploads
        // have no use here.
        ?.filter(
          (field) => field.type !== 'hidden' && !field.imageReferenceOnly,
        )
        .map((field) => (
          <FieldControl
            key={field.name}
            preset={preset}
            field={field}
            value={fields.get(field.name)}
            setField={setField}
            uploads={uploads}
          />
        ))}
    </div>
  );
}

function FieldControl({
  preset,
  field,
  value,
  setField,
  uploads,
}: {
  preset: Preset;
  field: InputField;
  value: any;
  setField: (name: string, value: any) => void;
  uploads?: WorkspaceUploads;
}) {
  const { t, lang } = useTemplateTranslation();
  const id = `field-${field.name}`;
  const label = t(field.label);
  // Most example placeholders exist only in English; elsewhere an empty box
  // reads cleaner than an English example, so show translated ones only.
  const translatedPlaceholder = field.placeholder ? t(field.placeholder) : '';
  const placeholder =
    lang === 'en' || translatedPlaceholder !== field.placeholder
      ? translatedPlaceholder
      : '';
  const hint = field.description ? t(field.description) : null;
  const hintId = hint ? `${id}-hint` : undefined;

  if (field.type === 'checkbox') {
    const checked = value === true;
    return (
      <div>
        <Switch.Group
          as='div'
          className='flex items-center justify-between gap-4 rounded-xl px-4 py-3 ring-1 ring-inset ring-[#0f0f28]/[0.08] dark:ring-white/10'
        >
          <Switch.Label
            className={clsxm(
              'cursor-pointer text-[14px] font-medium leading-5',
              tone.ink,
            )}
          >
            {label}
          </Switch.Label>
          <Switch
            checked={checked}
            onChange={(next: boolean) => setField(field.name, next)}
            aria-describedby={hintId}
            className={clsxm(
              'relative inline-flex h-6 w-11 flex-none cursor-pointer rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              checked ? 'bg-primary-600' : 'bg-[#dcdce6] dark:bg-white/15',
            )}
          >
            <span
              aria-hidden='true'
              className={clsxm(
                'pointer-events-none mt-0.5 inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200',
                checked
                  ? 'translate-x-[1.375rem] rtl:-translate-x-[1.375rem]'
                  : 'translate-x-0.5 rtl:-translate-x-0.5',
              )}
            />
          </Switch>
        </Switch.Group>
        {hint ? <Hint id={hintId}>{hint}</Hint> : null}
      </div>
    );
  }

  const length = typeof value === 'string' ? value.length : 0;
  const suggestions =
    lang === 'en' && field.name === 'keywords' && field.type === 'text'
      ? placeholderSuggestions(field.placeholder)
      : [];

  return (
    <div>
      <div className='flex min-h-[1.25rem] items-end justify-between gap-3'>
        <label
          htmlFor={id}
          className={clsxm('text-[14px] font-medium leading-5', tone.ink)}
        >
          {label}
        </label>
        <div className='flex flex-none items-center gap-3'>
          {field.enableMicrophone ? (
            <span className='text-[13px] font-medium'>
              <Microphone
                setTranscription={(text) =>
                  setField(
                    field.name,
                    `${typeof value === 'string' ? value : ''}${text}`,
                  )
                }
              />
            </span>
          ) : null}
          {/* Long limits (10,000 characters) are no constraint worth a counter. */}
          {field.limit &&
          Number(field.limit) <= 2000 &&
          (field.type === 'text' || field.type === 'textarea') ? (
            <span
              className={clsxm('text-xs tabular-nums', tone.quiet)}
              aria-hidden='true'
            >
              {length}/{field.limit}
            </span>
          ) : null}
        </div>
      </div>

      <div className='mt-2'>
        <Control
          id={id}
          preset={preset}
          field={field}
          value={value}
          placeholder={placeholder}
          hintId={hintId}
          setField={setField}
          uploads={uploads}
        />
      </div>

      {suggestions.length ? (
        <div className='mt-2.5 flex flex-wrap items-center gap-1.5'>
          <span className={clsxm('me-0.5 text-xs font-medium', tone.quiet)}>
            {t('Try')}
          </span>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type='button'
              onClick={() => setField(field.name, suggestion)}
              className={clsxm(
                'rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition',
                value === suggestion
                  ? 'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-400/10 dark:text-primary-200 dark:ring-primary-400/30'
                  : 'bg-white text-[#3c3c52] ring-[#0f0f28]/10 hover:ring-[#0f0f28]/25 dark:bg-white/[0.04] dark:text-[#c4c4da] dark:ring-white/10 dark:hover:ring-white/25',
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}

      {hint ? <Hint id={hintId}>{hint}</Hint> : null}
    </div>
  );
}

function Hint({ id, children }: { id?: string; children: string }) {
  return (
    <p id={id} className={clsxm('mt-2 text-[13px] leading-5', tone.quiet)}>
      {children}
    </p>
  );
}

function Control({
  id,
  preset,
  field,
  value,
  placeholder,
  hintId,
  setField,
  uploads,
}: {
  id: string;
  preset: Preset;
  field: InputField;
  value: any;
  placeholder: string;
  hintId?: string;
  setField: (name: string, value: any) => void;
  uploads?: WorkspaceUploads;
}) {
  const { t } = useTemplateTranslation();
  const { t: tTools } = useLocale('tools');
  const { t: tc } = useLocale('common');
  // Option labels live in `tools` or, for older selects, in `common`.
  const optionLabel = (option: string) => {
    const translated = tTools(option);
    return translated !== option ? translated : tc(option);
  };
  const limit = field.limit ? Number(field.limit) : undefined;
  const clamp = (text: string) =>
    limit && text.length > limit ? text.slice(0, limit) : text;

  switch (field.type) {
    case 'textarea':
      return (
        <TextareaAutosize
          id={id}
          name={field.name}
          minRows={field.minRows || 4}
          maxRows={18}
          value={typeof value === 'string' ? value : ''}
          placeholder={placeholder}
          aria-describedby={hintId}
          onChange={(event) => setField(field.name, clamp(event.target.value))}
          className={clsxm(inputClass, 'resize-none')}
        />
      );

    case 'select':
    case 'radio': {
      const options = fieldOptions(field);
      if (fitsAsChips(options)) {
        return (
          <RadioGroup
            value={value || null}
            onChange={(option: string) => setField(field.name, option)}
            aria-describedby={hintId}
            className='flex flex-wrap gap-2'
          >
            <RadioGroup.Label className='sr-only'>
              {t(field.label)}
            </RadioGroup.Label>
            {options.map((option) => (
              <RadioGroup.Option
                key={option}
                value={option}
                className={({ checked, active }) =>
                  clsxm(
                    'cursor-pointer select-none rounded-full px-3.5 py-2 text-[14px] font-medium leading-5 ring-1 ring-inset transition duration-150',
                    checked
                      ? 'bg-[#10101c] text-white ring-[#10101c] dark:bg-white dark:text-[#10101c] dark:ring-white'
                      : 'bg-white text-[#3c3c52] ring-[#0f0f28]/[0.12] hover:ring-[#0f0f28]/25 dark:bg-white/[0.04] dark:text-[#c4c4da] dark:ring-white/10 dark:hover:ring-white/25',
                    active &&
                      'outline outline-2 outline-offset-2 outline-primary-500',
                  )
                }
              >
                <RadioGroup.Label as='span'>
                  {optionLabel(option)}
                </RadioGroup.Label>
              </RadioGroup.Option>
            ))}
          </RadioGroup>
        );
      }
      return (
        <div className='relative'>
          <select
            id={id}
            name={field.name}
            value={typeof value === 'string' ? value : ''}
            aria-describedby={hintId}
            onChange={(event) => setField(field.name, event.target.value)}
            className={clsxm(
              inputClass,
              'cursor-pointer appearance-none bg-none pe-10',
            )}
          >
            <option value='' disabled hidden>
              {tc('Select an option')}
            </option>
            {options.map((option) => (
              <option key={option} value={option}>
                {optionLabel(option)}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            aria-hidden='true'
            className={clsxm(
              'pointer-events-none absolute end-3 top-1/2 h-5 w-5 -translate-y-1/2',
              tone.quiet,
            )}
          />
        </div>
      );
    }

    case 'file':
      return (
        <SingleUpload
          id={id}
          preset={preset}
          field={field}
          uploads={uploads}
          hintId={hintId}
        />
      );

    case 'files':
      return uploads ? (
        <MultiFileUpload
          onFilesChange={(files) => uploads.setMultiFiles(field.name, files)}
          accept={field.accept}
          acceptDescription={field.acceptDescription}
          maxFiles={field.maxFiles || 10}
          maxSizeBytes={(field.maxFileSize || 10) * 1024 * 1024}
          uploadPath={preset.slug}
          disabled={uploads.isFileLoading}
          initialFiles={uploads.uploadedMultiFiles[field.name] || {}}
        />
      ) : (
        <UploadLater />
      );

    // text, brand_voice and anything new render as a one-line input
    default:
      return (
        <input
          id={id}
          type='text'
          name={field.name}
          value={typeof value === 'string' ? value : ''}
          placeholder={placeholder}
          aria-describedby={hintId}
          autoComplete='off'
          onChange={(event) => setField(field.name, clamp(event.target.value))}
          className={inputClass}
        />
      );
  }
}

function SingleUpload({
  id,
  field,
  uploads,
  hintId,
}: {
  id: string;
  preset: Preset;
  field: InputField;
  uploads?: WorkspaceUploads;
  hintId?: string;
}) {
  const { t } = useTemplateTranslation();
  if (!uploads) return <UploadLater />;

  const uploaded = uploads.uploadedFiles[field.name];
  if (uploaded) {
    return (
      <div className='flex items-center gap-3 rounded-xl p-2.5 ring-1 ring-inset ring-[#0f0f28]/[0.12] dark:ring-white/10'>
        {uploaded.preview ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={uploaded.preview}
            alt=''
            className='h-12 w-12 flex-none rounded-lg object-cover'
          />
        ) : (
          <span className='flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-400/10 dark:text-primary-300'>
            <DocumentTextIcon className='h-6 w-6' aria-hidden='true' />
          </span>
        )}
        <span className={clsxm('min-w-0 flex-1 truncate text-sm', tone.ink)}>
          {uploaded.name}
        </span>
        <button
          type='button'
          onClick={() => uploads.removeFile(field.name)}
          aria-label={t('Remove file')}
          className={clsxm(
            'flex h-8 w-8 flex-none items-center justify-center rounded-full transition hover:bg-[#0f0f28]/[0.05] dark:hover:bg-white/10',
            tone.quiet,
          )}
        >
          <XIcon className='h-4 w-4' aria-hidden='true' />
        </button>
      </div>
    );
  }

  return (
    <label
      htmlFor={id}
      className={clsxm(
        'flex cursor-pointer items-center gap-3.5 rounded-xl border border-dashed border-[#0f0f28]/20 bg-[#fbfbfd] px-4 py-3.5 transition hover:border-primary-400 hover:bg-primary-50/40 dark:border-white/15 dark:bg-white/[0.03] dark:hover:border-primary-400/60',
        uploads.isFileLoading && 'pointer-events-none opacity-70',
      )}
    >
      <span className='flex h-10 w-10 flex-none items-center justify-center rounded-full bg-white text-primary-600 shadow-sm ring-1 ring-[#0f0f28]/[0.06] dark:bg-white/10 dark:text-primary-300 dark:ring-white/10'>
        {uploads.isFileLoading ? (
          <Spinner className='h-5 w-5 animate-spin text-primary-500' />
        ) : (
          <UploadIcon className='h-5 w-5' aria-hidden='true' />
        )}
      </span>
      <span className='min-w-0'>
        <span className={clsxm('block text-sm font-medium', tone.ink)}>
          {uploads.isFileLoading
            ? t('Uploading...')
            : t('Click to upload a file')}
        </span>
        {field.acceptDescription ? (
          <span className={clsxm('block text-xs', tone.quiet)}>
            {t(field.acceptDescription)}
          </span>
        ) : null}
      </span>
      <input
        id={id}
        type='file'
        className='sr-only'
        accept={field.accept}
        aria-describedby={hintId}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) uploads.uploadFile(field.name, file);
          event.target.value = '';
        }}
      />
    </label>
  );
}

/** Upload fields on sign-up-only templates: shown, but they wait. */
function UploadLater() {
  const { t } = useTemplateTranslation();
  return (
    <div
      className={clsxm(
        'flex items-center gap-3 rounded-xl border border-dashed border-[#0f0f28]/15 px-4 py-3.5 text-sm dark:border-white/15',
        tone.quiet,
      )}
    >
      <LockClosedIcon className='h-5 w-5 flex-none' aria-hidden='true' />
      {t('You can upload files after you sign up.')}
    </div>
  );
}
